import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import css from './view.module.css';
import Table from '../../../../components/common/table/table';
import GroupAnimal from '../../../../interfaces/group-animal';
import GroupAnimalPigs from '../../../../interfaces/group-animal-pigs';
import { useService } from '../../../../services/services';
import AnimalGroupToolbar from '../animals/components/group-animal/group-animal-toolbar';
import TableVerticalSizeFixer from '../../../../components/common/table/table-vertical-size-fixer/table-vertical-size-fixer';
import NotesModal from './notes/NotesModal';
import { SelectedRowsProvider } from '../../../../components/common/table/selected-rows-context';
import ShowIfAllowed from '../../../../components/common/show-if-allowed';
import { EditPen } from '../../../../assets/icons/insex';

export default function GroupAnimals() {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.livestock.animals_and_events.group_animals.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId } = useParams<{ farmId: string }>();
  const { groupAnimalService } = useService();
  const [animalGroupId, setAnimalGroupId] = useState('');
  const [groupAnimalDate, setGroupAnimalDate] = useState('');

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) => {
      setGroupAnimalDate(filter?.date);
      return groupAnimalService.index(
        farmId,
        { page, sort, desc, ...filter },
        true
      );
    },
    [groupAnimalService, farmId]
  );

  const toolbar = useCallback((): JSX.Element => {
    return (
      <ShowIfAllowed ability={{ title: 'create animal_event' }}>
        <AnimalGroupToolbar />
      </ShowIfAllowed>
    );
  }, []);

  const [isPigGroup, setIsPigGroup] = useState<boolean | null>(null);

  const useResponse = (responseData) => {
    useEffect(() => {
      if (responseData?.data[0]) {
        setIsPigGroup('count_all' in responseData?.data[0]);
      }
    }, [responseData]);
  };

  const columnsByGroupData = isPigGroup
    ? [
        {
          accessor: 'period',
          header: tp('period'),
          className: css['strong'],
        },
        {
          accessor: 'count_all',
          header: tp('animal_all'),
          className: css['strong'],
        },
        {
          accessor: 'count_vk',
          header: 'VK',
          className: css['special-fields'],
        },
        {
          accessor: 'count_sm',
          header: 'SM',
          className: css['special-fields'],
        },
        {
          accessor: 'count_ac',
          header: 'AC',
          className: css['special-fields'],
        },
        {
          accessor: 'count_jc',
          header: 'JC',
          className: css['special-fields'],
        },
        {
          accessor: 'count_nc',
          header: 'NC',
          className: css['special-fields'],
        },
        {
          accessor: 'count_as',
          header: 'AS',
          className: css['special-fields'],
        },
        {
          accessor: 'count_ps',
          header: 'PS',
          className: css['special-fields'],
        },
        {
          accessor: 'count_born',
          header: tp('animal_born'),
        },
        {
          accessor: 'count_slaughtered',
          header: tp('count_slaughtered'),
        },
        {
          accessor: 'count_fallen',
          header: tp('count_fallen'),
        },
        {
          accessor: 'count_sold',
          header: tp('count_sold'),
        },
      ]
    : [
        {
          accessor: 'species.value',
          header: tp('animal_group'),
          className: css['strong'],
          sortable: true,
        },
        {
          accessor: 'count',
          header: tp('count'),
          className: css['strong'],
          sortable: true,
        },
      ];

  const columns = [
    ...columnsByGroupData,
    {
      accessor: 'barn_ldc_nr',
      header: tp('barn'),
      sortable: !isPigGroup,
    },
    {
      accessor: 'herd_id',
      header: tp('herd'),
      sortable: !isPigGroup,
    },
    {
      accessor:
        groupAnimalDate === undefined ||
        groupAnimalDate === new Date().toISOString().split('T')[0]
          ? 'actual_id'
          : 'actual_group_animal.id',
      header: tp('notes'),
      valuable: (value) => {
        if (value) {
          return (
            <>
              <img
                src={EditPen}
                width={18}
                height={18}
                alt="animal group edit icon"
                onClick={() => setAnimalGroupId(value)}
                className={css['note-edit']}
              />
            </>
          );
        }
      },
    },
  ];

  return (
    <div className={css['body']}>
      <NotesModal
        animalGroupId={animalGroupId}
        setAnimalGroupId={setAnimalGroupId}
        apiRoutePrefix={isPigGroup ? '/pigs' : ''}
      />
      <SelectedRowsProvider<GroupAnimalPigs | GroupAnimal>>
        <TableVerticalSizeFixer>
          <Table<GroupAnimalPigs | GroupAnimal>
            request={request}
            keyAccessor="actual_id"
            toolbar={toolbar}
            responseData={useResponse}
            columns={columns}
            paginate={false}
            viewRecordAbility={{ title: 'view animal' }}
            filteredTotalsParams={{
              recordName: 'records',
            }}
          />
        </TableVerticalSizeFixer>
      </SelectedRowsProvider>
    </div>
  );
}
